// src/belediye/pages/sistem/SistemBirimAgaciPage.js
//
// FAZ-15 · 15C — Birim ağacı (mobil öncelikli). İç içe liste (genişlet/daralt);
// düğüm = ad + unit_type rozeti + üye/alt-birim sayısı. Düzenle: parent (kendi alt
// ağacı hariç), unit_type, bayraklar. parent yazma backend'de units.manage_tree ister.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PlusIcon,
  ChevronRightIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import usePermissions from "../../hooks/usePermissions";
import { fetchSistemUnits, createUnit, updateUnit } from "../../services/sistemApi";
import "./SistemBirimAgaci.css";

const backendError = (ex, fallback) => {
  const d = ex?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  if (d) {
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join(" · ");
  }
  return fallback;
};

// Düz listeden (parent bağlı) ağaç kur
const buildTree = (units) => {
  const byParent = new Map();
  units.forEach((u) => {
    const p = u.parent ?? null;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(u);
  });
  const attach = (parentId) =>
    (byParent.get(parentId) || [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "tr"))
      .map((u) => ({ ...u, children: attach(u.id) }));
  return attach(null);
};

// Bir düğümün kendisi + tüm alt ağacının id'leri (parent seçicide hariç tutmak için)
const subtreeIds = (units, rootId) => {
  const byParent = new Map();
  units.forEach((u) => {
    const p = u.parent ?? null;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(u);
  });
  const acc = new Set();
  const walk = (id) => {
    acc.add(id);
    (byParent.get(id) || []).forEach((c) => walk(c.id));
  };
  walk(rootId);
  return acc;
};

export default function SistemBirimAgaciPage() {
  const { can } = usePermissions();
  const canManage = can("units.manage") || can("units.manage_tree");

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [editUnit, setEditUnit] = useState(null); // {mode:"edit"|"create", unit, parentId}

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const u = await fetchSistemUnits();
      setUnits(Array.isArray(u) ? u : []);
    } catch {
      setError("Birimler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tree = useMemo(() => buildTree(units), [units]);

  const toggleCollapse = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node, depth = 0) => {
    const hasKids = node.children && node.children.length > 0;
    const isCollapsed = collapsed.has(node.id);
    return (
      <React.Fragment key={node.id}>
        <div
          className="sba-node"
          style={{ paddingLeft: `calc(${depth} * var(--space-md) + var(--space-sm))` }}
        >
          <button
            type="button"
            className={`sba-toggle${hasKids ? "" : " sba-toggle-empty"}`}
            onClick={() => hasKids && toggleCollapse(node.id)}
            aria-label={hasKids ? (isCollapsed ? "Genişlet" : "Daralt") : undefined}
            disabled={!hasKids}
          >
            {hasKids && (
              <ChevronRightIcon
                width={16}
                height={16}
                className={`sba-chevron${isCollapsed ? "" : " open"}`}
              />
            )}
          </button>

          <div className="sba-node-main">
            <span className="sba-node-name">{node.name}</span>
            <span className="sba-node-meta">
              {node.unit_type && (
                <span className="md-chip sba-type">{node.unit_type}</span>
              )}
              {!node.is_public && (
                <span className="md-chip">Vatandaşa kapalı</span>
              )}
              <span className="sba-counts">
                {node.member_count ?? 0} üye · {node.children_count ?? 0} alt birim
              </span>
            </span>
          </div>

          {canManage && (
            <div className="sba-node-actions">
              <button
                type="button"
                className="btn-md btn-md-ghost sba-icon-btn"
                title="Alt birim ekle"
                onClick={() =>
                  setEditUnit({ mode: "create", unit: null, parentId: node.id })
                }
              >
                <PlusIcon width={16} height={16} />
              </button>
              <button
                type="button"
                className="btn-md btn-md-ghost sba-icon-btn"
                title="Düzenle"
                onClick={() => setEditUnit({ mode: "edit", unit: node })}
              >
                <PencilSquareIcon width={16} height={16} />
              </button>
            </div>
          )}
        </div>
        {hasKids &&
          !isCollapsed &&
          node.children.map((c) => renderNode(c, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="page-container sba-root">
      <header className="sba-head">
        <div>
          <h1 className="sba-title">Birim Ağacı</h1>
          <p className="sba-subtitle">
            Organizasyon ağacını yönetin. Üst birim üyeliği alt ağacın tamamını
            kapsar.
          </p>
        </div>
        {canManage && (
          <button
            className="btn-md btn-md-primary sba-new-btn"
            onClick={() =>
              setEditUnit({ mode: "create", unit: null, parentId: null })
            }
          >
            <PlusIcon width={18} height={18} /> Yeni Kök Birim
          </button>
        )}
      </header>

      {loading ? (
        <div className="sba-state">Yükleniyor…</div>
      ) : error ? (
        <div className="sba-state sba-state-error">
          {error}
          <button className="btn-md btn-md-ghost" onClick={load}>
            Tekrar dene
          </button>
        </div>
      ) : tree.length === 0 ? (
        <div className="sba-state">Henüz birim yok.</div>
      ) : (
        <div className="md-card sba-tree">{tree.map((n) => renderNode(n))}</div>
      )}

      {editUnit && (
        <BirimModal
          mode={editUnit.mode}
          unit={editUnit.unit}
          parentId={editUnit.parentId}
          units={units}
          onClose={() => setEditUnit(null)}
          onSaved={async () => {
            setEditUnit(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function BirimModal({ mode, unit, parentId, units, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => ({
    name: unit?.name || "",
    slug: unit?.slug || "",
    unit_type: unit?.unit_type || "",
    parent: isEdit ? unit?.parent ?? "" : parentId ?? "",
    is_public: unit ? unit.is_public : true,
    is_staff_access_enabled: unit ? unit.is_staff_access_enabled : true,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setChk = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  // parent seçenekleri: düzenlenen düğümün kendisi + alt ağacı HARİÇ (döngü önlemi)
  const excluded = useMemo(
    () => (isEdit && unit ? subtreeIds(units, unit.id) : new Set()),
    [isEdit, unit, units]
  );
  const parentOptions = useMemo(
    () =>
      units
        .filter((u) => !excluded.has(u.id))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, "tr")),
    [units, excluded]
  );

  const parentChanged =
    isEdit && String(form.parent || "") !== String(unit?.parent ?? "");
  const affected = unit?.member_count ?? 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    const payload = {
      name: form.name,
      unit_type: form.unit_type || null,
      parent: form.parent === "" ? null : Number(form.parent),
      is_public: form.is_public,
      is_staff_access_enabled: form.is_staff_access_enabled,
    };
    if (!isEdit) payload.slug = form.slug;
    try {
      if (isEdit) await updateUnit(unit.id, payload);
      else await createUnit(payload);
      toast.success(isEdit ? "Birim güncellendi." : "Birim oluşturuldu.");
      await onSaved();
    } catch (ex) {
      setErr(backendError(ex, "Kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sba-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="sba-modal md-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="md-card-header">
          <h2 className="md-card-title">
            {isEdit ? "Birimi Düzenle" : "Yeni Birim"}
          </h2>
        </div>
        <form className="sba-form" onSubmit={submit} autoComplete="off">
          {err && <div className="sba-alert">{err}</div>}
          <label className="sba-field">
            <span>Ad</span>
            <input className="md-input" value={form.name} onChange={set("name")} required autoFocus />
          </label>
          {!isEdit && (
            <label className="sba-field">
              <span>Slug (URL kimliği)</span>
              <input className="md-input" value={form.slug} onChange={set("slug")} placeholder="orn: fen-isleri" required />
            </label>
          )}
          <label className="sba-field">
            <span>Birim türü (etiket — opsiyonel)</span>
            <input className="md-input" value={form.unit_type} onChange={set("unit_type")} placeholder="örn: Daire Başkanlığı" />
          </label>
          <label className="sba-field">
            <span>Üst birim</span>
            <select className="md-select" value={form.parent} onChange={set("parent")}>
              <option value="">— (kök birim)</option>
              {parentOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          {parentChanged && affected > 0 && (
            <div className="sba-warn">
              Üst birim değişiyor — bu düğümdeki ~{affected} üyenin (ve alt
              ağacının) kapsamı etkilenebilir.
            </div>
          )}
          <label className="sba-check-row">
            <input type="checkbox" checked={form.is_public} onChange={setChk("is_public")} />
            <span>Vatandaşa açık (başvuru alır)</span>
          </label>
          <label className="sba-check-row">
            <input type="checkbox" checked={form.is_staff_access_enabled} onChange={setChk("is_staff_access_enabled")} />
            <span>Personel erişimi açık</span>
          </label>
          <div className="sba-modal-actions">
            <button type="button" className="btn-md btn-md-ghost" onClick={onClose}>
              Vazgeç
            </button>
            <button type="submit" className="btn-md btn-md-primary" disabled={saving}>
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
